/**
 * SmartCell — wraps text intelligently:
 *  - When wrapText=false: single line, truncated (Excel compact mode)
 *  - When wrapText=true:  auto-grows up to maxLines, then scrolls internally.
 */
import React, { useRef, useEffect, useCallback } from 'react';

interface SmartCellProps {
    value: string;
    wrapText: boolean;
    isInput?: boolean;
    className?: string;
    onChange?: (val: string) => void;
    readOnly?: boolean;
    /** Max visual lines before internal scroll kicks in (default 6) */
    maxLines?: number;
}

const LINE_HEIGHT_PX = 20; // approximate px per line at text-sm

export const SmartCell: React.FC<SmartCellProps> = ({
    value,
    wrapText,
    isInput = false,
    className = '',
    onChange,
    readOnly = false,
    maxLines = 6,
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const maxHeightPx = maxLines * LINE_HEIGHT_PX;

    // Auto-resize textarea height based on content
    const resize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const newHeight = Math.min(el.scrollHeight, maxHeightPx);
        el.style.height = `${Math.max(newHeight, LINE_HEIGHT_PX)}px`;
        el.style.overflowY = el.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
    }, [maxHeightPx]);

    useEffect(() => {
        if (wrapText) resize();
    }, [value, wrapText, resize]);

    if (!wrapText) {
        // Compact mode — single line truncated
        if (isInput && !readOnly && onChange) {
            return (
                <input
                    type="text"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    className={`w-full bg-transparent rounded border border-transparent hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 truncate text-sm p-1 ${className}`}
                />
            );
        }
        return <span className={`block truncate text-sm ${className}`}>{value}</span>;
    }

    // Wrap mode
    if (isInput && !readOnly && onChange) {
        return (
            <textarea
                ref={textareaRef}
                value={value}
                onChange={e => { onChange(e.target.value); resize(); }}
                rows={1}
                style={{ resize: 'none', overflowY: 'hidden', minHeight: `${LINE_HEIGHT_PX}px` }}
                className={`w-full bg-transparent rounded border border-transparent hover:border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 text-sm p-1 leading-5 ${className}`}
            />
        );
    }

    return (
        <div
            style={{
                maxHeight: `${maxHeightPx}px`,
                overflowY: 'auto',
                lineHeight: '20px',
            }}
            className={`text-sm whitespace-pre-wrap break-words leading-5 ${className}`}
        >
            {value || <span className="text-text-secondary italic text-xs">—</span>}
        </div>
    );
};

export default SmartCell;
